import { createContext, useContext, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/api/client';
import { billingApi, opsApi } from '@/api/ops';
import { PERMISSIONS, type PlanOption, type SubscriptionDetail } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Callout } from '@/components/Callout';
import { Select, Segmented } from '@/components/Select';
import { StatTile } from '@/components/StatTile';
import { ReasonField, reasonOk } from '@/components/ops/Forms';
import { ConfirmDialog } from '@/components/ops/Modal';
import { formatMoney, humanizeStatus, statusTone } from '@/components/subscription';
import { minorToMajorText, parseMajorToMinor } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { formatWhen } from './AccountsPage';

/** The six subscription operations, bound to one IAM: the configured one, or an environment's. */
interface SubscriptionOps {
  get: (tenantId: string) => Promise<SubscriptionDetail>;
  plans: (tenantId: string) => Promise<PlanOption[]>;
  changePlan: typeof api.subscription.changePlan;
  setSeats: typeof api.subscription.setSeats;
  extendTrial: typeof api.subscription.extendTrial;
  setOverride: typeof api.subscription.setOverride;
  clearOverride: typeof api.subscription.clearOverride;
  changeStatus: typeof api.subscription.changeStatus;
}

/**
 * Tenant ops (control plane v1): the same operations against ANY managed environment's IAM through the
 * console's environment client (docs/api-tenant-ops.md §7). Same bodies, same answers.
 */
function environmentOps(env: string): SubscriptionOps {
  return {
    get: (tenantId) => opsApi.subscription.get(env, tenantId),
    plans: (tenantId) => opsApi.subscription.plans(env, tenantId),
    changePlan: (tenantId, body) => opsApi.subscription.changePlan(env, tenantId, body),
    setSeats: (tenantId, seatLimit) => opsApi.subscription.setSeats(env, tenantId, seatLimit),
    extendTrial: (tenantId, days) => opsApi.subscription.extendTrial(env, tenantId, days),
    setOverride: (tenantId, body) => opsApi.subscription.setOverride(env, tenantId, body),
    clearOverride: (tenantId, appKey) => opsApi.subscription.clearOverride(env, tenantId, appKey),
    changeStatus: (tenantId, status, reason) => opsApi.subscription.changeStatus(env, tenantId, status, reason),
  };
}

const OpsContext = createContext<SubscriptionOps>(api.subscription);

/**
 * The Billing tab: live subscription state from anyware.iam, plus the six ops actions.
 *
 * The ONLY surface in this app that reads live from IAM rather than from the replica. That is
 * deliberate: deciding whether to raise a seat limit needs today's seat count and today's override
 * list, not figures that may be three hours old. Everything else on the account page still renders
 * with the platform down; this panel says so instead.
 *
 * With `environment` the operations go to THAT environment's IAM (tenant ops); without it, to the
 * console's configured IAM (the original Accounts route).
 */
export function AccountBillingTab({ tenantId, environment }: { tenantId: string; environment?: string }) {
  const queryClient = useQueryClient();
  const ops = environment ? environmentOps(environment) : api.subscription;
  const scope = environment ?? 'configured';

  const subscription = useQuery({
    queryKey: ['subscription', scope, tenantId],
    queryFn: () => ops.get(tenantId),
    retry: false,
  });

  const plans = useQuery({
    queryKey: ['plans', scope],
    queryFn: () => ops.plans(tenantId),
    retry: false,
    // The catalog changes on platform releases, not during a session.
    staleTime: 15 * 60 * 1000,
  });

  /** Replaces the cached subscription with whatever the mutation returned — no re-fetch. */
  function applyResult(updated: SubscriptionDetail) {
    queryClient.setQueryData(['subscription', scope, tenantId], updated);
    // The replica is now behind by definition: the Accounts list will still show the old plan until
    // the next collector run. Invalidating makes it re-fetch, which at least drops any locally
    // cached copy of a figure we know to be stale.
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    queryClient.invalidateQueries({ queryKey: ['account', tenantId] });
  }

  if (subscription.isLoading) {
    return <div className="empty-state"><span className="spinner" /></div>;
  }

  if (subscription.isError) {
    const err = subscription.error;
    const status = err instanceof ApiError ? err.status : 0;

    // 404 is a real, actionable state rather than a failure — and the consequence is worth spelling
    // out, because a tenant with no subscription row is one whose entitlements are unenforced.
    if (status === 404) {
      return (
        <Callout tone="warning" title="This tenant has no subscription in anyware.iam">
          IAM's feature gate <strong>fails open</strong> for a tenant with no subscription row, so this
          account currently has every app regardless of what it pays. Close the gap with{' '}
          <code>POST /api/v1/iam/internal/subscriptions/backfill</code>.
        </Callout>
      );
    }

    return (
      <Callout tone="error" title="Could not reach anyware.iam">
        {err instanceof ApiError ? err.message : 'Unexpected error.'}
        <div className="t-caption muted" style={{ marginTop: 6 }}>
          Everything else on this page still works — it comes from the local replica. Only this panel
          and the ops actions need IAM to be up.
        </div>
      </Callout>
    );
  }

  const s = subscription.data!;
  // Reading needs subscription:read; every change needs subscription:manage (owner). A role without it sees the state
  // and no buttons — never a button that can only answer 403.
  const canManage = hasPermission(PERMISSIONS.subscriptionManage);

  return (
    <OpsContext.Provider value={ops}>
      <SubscriptionSummary subscription={s} />
      {!canManage && (
        <p className="t-caption muted" style={{ margin: 0 }}>Your role can see this subscription but not change it.</p>
      )}

      {!s.plan_is_known && (
        <Callout tone="warning" title={`Plan "${s.plan_code}" is no longer in IAM's catalog`}>
          It was retired after this subscription was created. Entitlements resolve from overrides only
          until the account is moved to a current plan.
        </Callout>
      )}

      {s.cancel_at_period_end && (
        <Callout tone="warning" title="Cancelled — ends at the period end">
          Access is unchanged until {s.current_period_end ? formatWhen(s.current_period_end) : 'the period end'};
          IAM's sweep will churn it then. Reactivating clears the cancellation.
        </Callout>
      )}

      {s.status === 'past_due' && (
        <Callout tone="info" title="Past due — access is deliberately unchanged">
          Cutting a paying customer off the moment a card fails destroys more revenue than it protects.
          {s.grace_period_ends_at && ` Grace ends ${formatWhen(s.grace_period_ends_at)}.`}
        </Callout>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
        <PlanCard
          environment={environment}
          subscription={s}
          plans={plans.data ?? []}
          plansFailed={plans.isError}
          tenantId={tenantId}
          onApplied={applyResult}
          canManage={canManage}
        />
        <EntitlementsCard subscription={s} tenantId={tenantId} onApplied={applyResult} canManage={canManage} />
      </div>

      <LifecycleCard subscription={s} tenantId={tenantId} onApplied={applyResult} canManage={canManage} />
      <HistoryCard subscription={s} />
    </OpsContext.Provider>
  );
}

function SubscriptionSummary({ subscription: s }: { subscription: SubscriptionDetail }) {
  const tone = statusTone(s.status);

  return (
    <div className="kpi-row">
      <StatTile
        label="Plan"
        value={s.plan_display_name}
        sub={s.is_billable ? s.billing_cycle : 'never billed'}
      />
      <StatTile
        label="Status"
        value={humanizeStatus(s.status)}
        tone={tone === 'danger' ? 'critical' : tone === 'warning' ? 'warning' : 'default'}
      />
      <StatTile
        label="Price"
        value={formatMoney(s.price_minor, s.currency)}
        sub={`per ${s.billing_cycle === 'annual' ? 'year' : 'month'}`}
      />
      <StatTile
        label="Seats"
        // '∞' rather than '—' when unenforced: the limit is absent on purpose, not unknown. And '?'
        // for an unreadable seat count, never 0.
        value={`${s.seats_used ?? '?'} / ${s.seat_limit ?? '∞'}`}
        sub={s.seat_limit === null ? 'limit not enforced' : 'live count from the tenant DB'}
        tone={s.seat_limit !== null && (s.seats_used ?? 0) >= s.seat_limit ? 'warning' : 'default'}
      />
    </div>
  );
}

/** The billing SKU of a plan + cycle (`plan.team.monthly`) — what a negotiated price overrides. */
export function planSku(planCode: string, cycle: string): string {
  return `plan.${planCode.trim().toLowerCase()}.${cycle === 'annual' ? 'annual' : 'monthly'}`;
}

function PlanCard({
  environment,
  subscription: s,
  plans,
  plansFailed,
  tenantId,
  onApplied,
  canManage,
}: {
  /** The managed environment — an agreed price is a BILLING override there; without one it cannot be set here. */
  environment?: string;
  subscription: SubscriptionDetail;
  plans: PlanOption[];
  plansFailed: boolean;
  tenantId: string;
  onApplied: (updated: SubscriptionDetail) => void;
  canManage: boolean;
}) {
  const [planCode, setPlanCode] = useState(s.plan_code);
  const [cycle, setCycle] = useState(s.billing_cycle || 'monthly');
  // Kept as a STRING so an empty field means "take the plan's list price" and is distinguishable
  // from a deliberate 0 — which is a real, meaningful price for a comped account.
  const [priceMajor, setPriceMajor] = useState('');
  const [seatLimit, setSeatLimit] = useState(s.seat_limit === null ? '' : String(s.seat_limit));
  const [confirm, setConfirm] = useState<'plan' | 'seats' | null>(null);
  const [priceReason, setPriceReason] = useState('');
  const ops = useContext(OpsContext);
  const queryClient = useQueryClient();
  // An agreed price is billing's: a per-tenant price override on the plan SKU, with a reason (money review 2026-10-04) —
  // never IAM's price field, which billing overwrites from the price book.
  const canPrice = !!environment && hasPermission(PERMISSIONS.billingManage);
  const seesOverrides = !!environment && hasPermission(PERMISSIONS.billingRead);
  const overrides = useQuery({
    queryKey: ['ops', 'billing', environment, 'overrides', tenantId],
    queryFn: () => billingApi.overrides(environment!, tenantId),
    enabled: seesOverrides,
    ...LIVE,
  });
  const currentSku = planSku(s.plan_code, s.billing_cycle || 'monthly');
  const agreed = overrides.data?.overrides.find((o) => o.active && o.sku === currentSku && o.amount !== null) ?? null;

  // The typed price in the currency's OWN minor units (KWD has 3 decimals, JPY none) — never "× 100".
  const price = priceMajor.trim() === '' ? null : parseMajorToMinor(priceMajor, s.currency || 'USD');
  const seats = seatLimit.trim() === '' ? null : Number(seatLimit);
  const seatsInvalid = seats !== null && (!Number.isInteger(seats) || seats < 1);

  const planChanged = planCode !== s.plan_code || cycle !== (s.billing_cycle || 'monthly');
  const changePlan = useMutation({
    mutationFn: async () => {
      // The override first: if billing refuses it nothing has changed; a plan change after it can only make it apply.
      if (price && price.ok && environment) {
        await billingApi.createOverride(environment, {
          tenant_id: tenantId,
          sku: planSku(planCode, cycle),
          currency: s.currency,
          amount: price.minor,
          markup_bps: null,
          reason: priceReason.trim(),
        });
        await queryClient.invalidateQueries({ queryKey: ['ops', 'billing', environment] });
      }
      return planChanged
        ? ops.changePlan(tenantId, { plan_code: planCode, billing_cycle: cycle, currency: s.currency })
        : null;
    },
    onSuccess: (updated) => {
      setConfirm(null);
      setPriceMajor('');
      setPriceReason('');
      if (updated) onApplied(updated);
    },
  });

  const setSeats = useMutation({
    // Empty CLEARS the limit. Passing null is the documented way to make seats unenforced; it is
    // never interpreted as zero.
    mutationFn: () => ops.setSeats(tenantId, seats),
    onSuccess: (updated) => { setConfirm(null); onApplied(updated); },
  });

  const selected = plans.find((p) => p.plan_code === planCode);
  const listPrice = selected?.list_price_minor ?? s.price_minor;
  const planName = (code: string) => plans.find((p) => p.plan_code === code)?.display_name ?? code;
  const newPriceText = price && price.ok
    ? `${formatMoney(price.minor, s.currency)} (agreed — a billing price override)`
    : `the list price (${formatMoney(listPrice, s.currency)})`;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Plan &amp; seats</h2>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 12 }}>
        {plansFailed && (
          <Callout tone="warning" title="Could not load the plan catalog">
            The plan picker is limited to the current plan until IAM responds.
          </Callout>
        )}

        {!canManage ? (
          <div className="t-body">
            {s.plan_display_name} · {s.billing_cycle || 'monthly'} · {formatMoney(s.price_minor, s.currency)} ·{' '}
            {s.seat_limit === null ? 'seats not limited' : `${s.seat_limit} seats`}
          </div>
        ) : (
          <>
            <Select
              label="Plan"
              value={planCode}
              onChange={setPlanCode}
              options={[
                // The current plan is always an option, even when it has been retired from the catalog
                // — otherwise the picker would silently show a different plan than the account is on.
                ...(plans.some((p) => p.plan_code === s.plan_code)
                  ? []
                  : [{ value: s.plan_code, label: `${s.plan_code} (current, not in catalog)` }]),
                ...plans.map((p) => ({
                  value: p.plan_code,
                  label: `${p.display_name} — ${formatMoney(p.list_price_minor, s.currency)}`,
                  hint: p.seat_limit === null ? 'unlimited seats' : `${p.seat_limit} seats`,
                })),
              ]}
            />

            {selected && (
              <div className="t-caption muted">
                Grants: {selected.apps.join(', ') || 'no apps'}
                {selected.ai_credit_grant !== null &&
                  ` · ${selected.ai_credit_grant.toLocaleString('en-US')} AI credits/mo`}
              </div>
            )}

            {/* Two mutually exclusive choices, both always visible — Segmented, not a dropdown that
                hides half the answer behind a click. */}
            <Segmented
              label="Billing cycle"
              value={cycle}
              onChange={setCycle}
              options={[{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]}
            />

            {agreed && (
              <div className="t-caption">
                Agreed price now: <strong>{formatMoney(agreed.amount, agreed.currency)}</strong> (billing override — {agreed.reason})
              </div>
            )}
            {canPrice ? (
              <label className="t-label" style={{ display: 'grid', gap: 4 }}>
                Agreed price ({s.currency}) — blank uses the plan&apos;s list price
                <input
                  className="input"
                  inputMode="decimal"
                  placeholder={minorToMajorText(listPrice, s.currency || 'USD')}
                  value={priceMajor}
                  onChange={(e) => setPriceMajor(e.target.value)}
                  aria-invalid={price !== null && !price.ok ? true : undefined}
                />
                {price !== null && !price.ok && <span className="t-caption note-error">{price.error}</span>}
                <span className="t-caption muted">Saved in billing as a price override on {planSku(planCode, cycle)}, with a reason.</span>
              </label>
            ) : (
              <p className="t-caption muted" style={{ margin: 0 }}>
                {environment
                  ? 'An agreed price is a billing price override — your role cannot write billing.'
                  : 'An agreed price is a billing price override — open this tenant on its environment page to set one.'}
              </p>
            )}

            <button
              type="button"
              className="btn btn-primary"
              disabled={changePlan.isPending || (price !== null && !price.ok) || (!planChanged && !(price && price.ok))}
              onClick={() => setConfirm('plan')}
            >
              {!planChanged && price && price.ok ? 'Set agreed price…' : 'Change plan…'}
            </button>

            <hr style={{ border: 0, borderTop: '1px solid var(--border-light)', margin: '4px 0' }} />

            <label className="t-label" style={{ display: 'grid', gap: 4 }}>
              Seat limit — blank means not limited (never zero)
              <input
                className="input"
                type="number"
                min="1"
                placeholder="not limited"
                value={seatLimit}
                onChange={(e) => setSeatLimit(e.target.value)}
                aria-invalid={seatsInvalid || undefined}
              />
              {seatsInvalid && <span className="t-caption note-error">A whole number of at least 1, or blank.</span>}
            </label>
            <button type="button" className="btn btn-ghost" disabled={setSeats.isPending || seatsInvalid} onClick={() => setConfirm('seats')}>
              Set seat limit…
            </button>
          </>
        )}
      </div>

      {confirm === 'plan' && (
        <ConfirmDialog title={planChanged ? 'Change the plan?' : 'Set the agreed price?'} confirmLabel={planChanged ? 'Change plan' : 'Set agreed price'}
          pending={changePlan.isPending}
          confirmDisabled={!!(price && price.ok) && !reasonOk(priceReason)}
          onConfirm={() => changePlan.mutate()} onCancel={() => { setConfirm(null); changePlan.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            {planChanged
              ? <>From <strong>{s.plan_display_name}</strong> ({s.billing_cycle || 'monthly'}) to <strong>{planName(planCode)}</strong> ({cycle}),
                at {newPriceText} per {cycle === 'annual' ? 'year' : 'month'}. The apps follow the new plan at once.</>
              : <>{s.plan_display_name} ({cycle}) at {newPriceText} per {cycle === 'annual' ? 'year' : 'month'}.</>}
          </p>
          {price && price.ok && <ReasonField value={priceReason} onChange={setPriceReason} />}
          <MutationError error={changePlan.error} />
        </ConfirmDialog>
      )}
      {confirm === 'seats' && (
        <ConfirmDialog title="Change the seat limit?" confirmLabel="Set seat limit" pending={setSeats.isPending}
          onConfirm={() => setSeats.mutate()} onCancel={() => { setConfirm(null); setSeats.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            From {s.seat_limit === null ? 'not limited' : `${s.seat_limit} seats`} to{' '}
            <strong>{seats === null ? 'not limited' : `${seats} seats`}</strong>.
            {seats !== null && s.seats_used !== null && seats < s.seats_used &&
              ` The tenant already uses ${s.seats_used} — nobody is removed, but no one new can be added.`}
          </p>
          <MutationError error={setSeats.error} />
        </ConfirmDialog>
      )}
    </div>
  );
}

type OverrideAction = { kind: 'grant' | 'deny' | 'clear'; app: string };

/**
 * Per-app entitlement overrides.
 *
 * Shows the resolved set next to the plan's set, because the whole reason overrides exist is that
 * the two can legitimately differ — and an exception nobody can see becomes folklore. Grant and deny ask for a reason
 * (recorded on the override); every change is confirmed.
 */
function EntitlementsCard({
  subscription: s,
  tenantId,
  onApplied,
  canManage,
}: {
  subscription: SubscriptionDetail;
  tenantId: string;
  onApplied: (updated: SubscriptionDetail) => void;
  canManage: boolean;
}) {
  const [pending, setPending] = useState<OverrideAction | null>(null);
  const [reason, setReason] = useState('');
  const ops = useContext(OpsContext);
  const done = (updated: SubscriptionDetail) => { setPending(null); setReason(''); onApplied(updated); };

  const apply = useMutation({
    mutationFn: (action: OverrideAction) => action.kind === 'clear'
      ? ops.clearOverride(tenantId, action.app)
      : ops.setOverride(tenantId, { app_key: action.app, effect: action.kind, reason: reason.trim() }),
    onSuccess: done,
  });

  const entitled = new Set(s.resolved_apps);
  const overrideByApp = new Map(s.overrides.map((o) => [o.app_key, o]));

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>App entitlements</h2>
        <span className="t-label subtle">a “deny” always wins</span>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 12 }}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>App</th><th>Can use it</th><th>Why</th>{canManage && <th />}</tr>
            </thead>
            <tbody>
              {s.catalog_apps.map((app) => {
                const override = overrideByApp.get(app);
                const isEntitled = entitled.has(app);

                return (
                  <tr key={app}>
                    <td><span className="mono t-label">{app}</span></td>
                    <td style={{ color: isEntitled ? 'var(--success)' : 'var(--text-subtle)' }}>
                      {isEntitled ? 'yes' : 'no'}
                    </td>
                    <td>
                      {override ? (
                        <span
                          className={override.effect === 'deny' ? 'chip chip-down' : 'chip chip-on'}
                          title={override.reason ?? 'no reason recorded'}
                        >
                          {override.effect === 'deny' ? 'denied by hand' : 'granted by hand'}
                        </span>
                      ) : (
                        <span className="subtle">the plan</span>
                      )}
                    </td>
                    {canManage && (
                      <td style={{ display: 'flex', gap: 4 }}>
                        {override?.effect !== 'grant' && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPending({ kind: 'grant', app })}>grant</button>
                        )}
                        {override?.effect !== 'deny' && (
                          <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setPending({ kind: 'deny', app })}>deny</button>
                        )}
                        {override && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPending({ kind: 'clear', app })}>back to plan</button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {pending && (
        <ConfirmDialog
          title={pending.kind === 'clear' ? `Return ${pending.app} to the plan?` : `${pending.kind === 'grant' ? 'Grant' : 'Deny'} ${pending.app}?`}
          confirmLabel={pending.kind === 'clear' ? 'Back to plan' : pending.kind === 'grant' ? 'Grant' : 'Deny'}
          destructive={pending.kind === 'deny'}
          pending={apply.isPending}
          confirmDisabled={pending.kind !== 'clear' && !reasonOk(reason)}
          onConfirm={() => apply.mutate(pending)}
          onCancel={() => { setPending(null); setReason(''); apply.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            {pending.kind === 'deny'
              ? `Nobody in this tenant can open ${pending.app}, whatever the plan says, until the override is removed.`
              : pending.kind === 'grant'
                ? `The tenant can open ${pending.app} even if the plan does not include it.`
                : `${pending.app} follows the plan again.`}
          </p>
          {pending.kind !== 'clear' && (
            <ReasonField value={reason} onChange={setReason} label="Reason (required — recorded on the override)" />
          )}
          <MutationError error={apply.error} />
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * The status actions and the trial extension.
 *
 * Only transitions IAM's state machine actually allows are offered, so the UI cannot present a
 * button that is guaranteed to 422. The rule is mirrored here rather than fetched, and mirroring it
 * costs nothing because IAM re-validates every request anyway — the worst case is a legal action
 * being hidden, never an illegal one succeeding.
 */
const NEXT_STATES: Record<string, { status: string; label: string; destructive?: boolean; effect: string }[]> = {
  trialing: [
    { status: 'active', label: 'Mark active', effect: 'The trial ends and the account is active.' },
    { status: 'cancelled', label: 'Cancel', effect: 'Access continues until the period end, then the account is churned.' },
    { status: 'suspended', label: 'Suspend', destructive: true, effect: 'Every app is denied; only the owner can still sign in.' },
  ],
  active: [
    { status: 'past_due', label: 'Mark past due', effect: 'Access is unchanged; the grace period starts.' },
    { status: 'cancelled', label: 'Cancel', effect: 'Access continues until the period end, then the account is churned.' },
    { status: 'suspended', label: 'Suspend', destructive: true, effect: 'Every app is denied; only the owner can still sign in.' },
  ],
  past_due: [
    { status: 'active', label: 'Mark paid (reactivate)', effect: 'The account is active again.' },
    { status: 'cancelled', label: 'Cancel', effect: 'Access continues until the period end, then the account is churned.' },
    { status: 'suspended', label: 'Suspend', destructive: true, effect: 'Every app is denied; only the owner can still sign in.' },
  ],
  suspended: [
    { status: 'active', label: 'Reactivate', effect: 'The apps work again.' },
    { status: 'churned', label: 'Mark churned', destructive: true, effect: 'The account counts as lost; its data is kept until the retention date.' },
  ],
  cancelled: [
    { status: 'active', label: 'Reactivate', effect: 'The cancellation is cleared.' },
    { status: 'churned', label: 'Mark churned', destructive: true, effect: 'The account counts as lost; its data is kept until the retention date.' },
  ],
  churned: [{ status: 'active', label: 'Reactivate', effect: 'The account is active again.' }],
};

function LifecycleCard({
  subscription: s,
  tenantId,
  onApplied,
  canManage,
}: {
  subscription: SubscriptionDetail;
  tenantId: string;
  onApplied: (updated: SubscriptionDetail) => void;
  canManage: boolean;
}) {
  const [reason, setReason] = useState('');
  const [trialDays, setTrialDays] = useState('14');
  const [pending, setPending] = useState<(typeof NEXT_STATES)[string][number] | 'trial' | null>(null);
  const ops = useContext(OpsContext);
  const close = () => { setPending(null); setReason(''); };

  const changeStatus = useMutation({
    mutationFn: (status: string) => ops.changeStatus(tenantId, status, reason.trim()),
    onSuccess: (updated) => { close(); onApplied(updated); },
  });

  const days = Number(trialDays);
  const daysInvalid = !Number.isInteger(days) || days < 1 || days > 365;
  const extendTrial = useMutation({
    mutationFn: () => ops.extendTrial(tenantId, days),
    onSuccess: (updated) => { close(); onApplied(updated); },
  });

  const targets = NEXT_STATES[s.status] ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Lifecycle</h2>
        <span className="t-label subtle">currently {humanizeStatus(s.status)}</span>
      </div>
      <div className="card-body" style={{ display: 'grid', gap: 12 }}>
        <div className="t-caption muted">
          Suspending denies every app and lets only the tenant OWNER sign in — deliberately, so they
          can still fix their billing. Nothing here deletes data.
        </div>

        {canManage && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {targets.map((t) => (
              <button
                key={t.status}
                type="button"
                className={`btn btn-ghost${t.destructive ? ' is-danger' : ''}`}
                onClick={() => setPending(t)}
              >
                {t.label}…
              </button>
            ))}
            {targets.length === 0 && (
              <span className="t-caption subtle">
                No transitions available from "{s.status}" — IAM does not recognise this status.
              </span>
            )}
          </div>
        )}

        {canManage && <hr style={{ border: 0, borderTop: '1px solid var(--border-light)', margin: '4px 0' }} />}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {canManage && (
            <>
              <label className="t-label" style={{ display: 'grid', gap: 4 }}>
                Extend trial by (days)
                <input
                  className="input"
                  type="number"
                  min="1"
                  max="365"
                  style={{ width: 120 }}
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                  aria-invalid={daysInvalid || undefined}
                />
              </label>
              <button type="button" className="btn btn-ghost" disabled={daysInvalid} onClick={() => setPending('trial')}>
                Extend trial…
              </button>
            </>
          )}
          <span className="t-caption muted">
            {s.trial_ends_at
              ? `The trial ends ${formatWhen(s.trial_ends_at)}.${canManage ? ' Extending a lapsed trial measures from today and returns the account to trialing.' : ''}`
              : `No trial recorded${canManage ? ' — extending starts one from today' : ''}.`}
          </span>
        </div>
      </div>

      {pending === 'trial' && (
        <ConfirmDialog title={`Extend the trial by ${days} days?`} confirmLabel="Extend trial" pending={extendTrial.isPending}
          onConfirm={() => extendTrial.mutate()} onCancel={() => { close(); extendTrial.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            {s.trial_ends_at ? `It currently ends ${formatWhen(s.trial_ends_at)}.` : 'No trial is recorded; one starts today.'}
          </p>
          <MutationError error={extendTrial.error} />
        </ConfirmDialog>
      )}
      {pending && pending !== 'trial' && (
        <ConfirmDialog title={`${pending.label}?`} confirmLabel={pending.label} destructive={pending.destructive}
          pending={changeStatus.isPending} confirmDisabled={!reasonOk(reason)}
          onConfirm={() => changeStatus.mutate(pending.status)} onCancel={() => { close(); changeStatus.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            From {humanizeStatus(s.status)} to <strong>{humanizeStatus(pending.status)}</strong>. {pending.effect}
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={changeStatus.error} />
        </ConfirmDialog>
      )}
    </div>
  );
}

/** IAM's append-only trail — the authoritative history, not the replica's diff-derived events. */
function HistoryCard({ subscription: s }: { subscription: SubscriptionDetail }) {
  if (s.events.length === 0) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>History</h2>
        <span className="t-label subtle">from anyware.iam, newest first</span>
      </div>
      <table className="table">
        <thead>
          <tr><th>When</th><th>What</th><th>Who</th></tr>
        </thead>
        <tbody>
          {s.events.map((e, i) => (
            <tr key={`${e.occurred_at}-${i}`}>
              <td className="muted">{formatWhen(e.occurred_at)}</td>
              <td>
                {e.summary}
                {/* A backfilled row is not a commercial decision, and must never be read as one. */}
                {e.kind === 'backfilled' && (
                  <span className="badge-muted" title="Created by the migration, not by a commercial decision">
                    backfill
                  </span>
                )}
              </td>
              <td className="muted">{e.actor_label ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A failed mutation, shown inline next to the control that caused it.
 *
 * IAM's status is preserved end to end, so a 409 (seat conflict) and a 422 (illegal transition) read
 * differently here — and the detail body, which names the legal alternatives, is shown rather than
 * swallowed.
 */
function MutationError({ error }: { error: unknown }) {
  if (!error) return null;

  const message = error instanceof ApiError ? error.message : 'Unexpected error.';
  const unavailable = error instanceof ApiError && error.status === 503;
  // The application code, verbatim, when the answer carried one (tenant ops problem bodies do).
  const code = error instanceof ApiError ? error.code : null;

  return (
    <Callout tone={unavailable ? 'warning' : 'error'} title={unavailable ? 'anyware.iam is unreachable' : 'Refused'}>
      {code && <span className="code-chip mono">{code}</span>}
      {message}
      {unavailable && (
        <div className="t-caption muted" style={{ marginTop: 6 }}>
          IAM did not confirm the change — a timed-out write may still have landed. Nothing was retried;
          reload this page to see the current state before trying again.
        </div>
      )}
    </Callout>
  );
}
